import { useState, useCallback } from 'react';

const SEEN_KEY = 'hbs:seenWelcomeBanner';
const ARTICLE_URL = 'https://heybesafe.com/support/how-do-i-use-hey-be-safe/';

function alreadySeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function markSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* ignore */
  }
}

/**
 * One-time welcome banner shown the first time the web app loads on a given
 * browser. Points new users at the "How do I use Hey Be Safe?" support article
 * (opens in a new tab). Dismissing it — or opening the article — sets a
 * localStorage flag so it never shows again on this browser.
 *
 * Rendered within AppShell's banner stack, which is only present once the user
 * is authenticated (same gating as the sibling banners), so no auth check is
 * needed here.
 */
export function WelcomeBanner(): JSX.Element | null {
  const [seen, setSeen] = useState<boolean>(alreadySeen);

  const dismiss = useCallback(() => {
    markSeen();
    setSeen(true);
  }, []);

  if (seen) return null;

  return (
    <div className="bg-yellow-100 border-b border-yellow-400 px-4 py-2 flex items-center justify-between gap-4">
      <span className="text-sm text-yellow-950">
        New to Hey Be Safe? See how to set it up on your browser and phone.{' '}
        <a
          href={ARTICLE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={dismiss}
          className="text-yellow-950 underline font-medium hover:text-yellow-700"
        >
          Using Hey Be Safe
        </a>
      </span>
      <button
        id="welcome-banner-dismiss-button"
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="text-yellow-950 hover:text-yellow-700 text-sm"
      >
        ×
      </button>
    </div>
  );
}
